import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { GrChapterAdd } from "react-icons/gr";
import { Button, ButtonBlue } from "@/Components/Button/Button";
import api from "@/Api/Api";
import Modal from "@shared/Components/Modal/Modal";
import ModalContent from "@shared/Components/Modal/ModalContent";
import ModalButtons from "@shared/Components/Modal/ModalButtons";
import { Entities } from "@shared/Entities/Entities";
import W_TextInput from "@shared/Components/FormComponents/TextInput/W_TextInput";
import { Action, Ticket } from "@shared/Entities/EntityTypes";
import { FormHotkeys } from "@shared/Components/Utils/FormHotkeys";

interface ActionContextMenuProps {
	ticket: Ticket;
	action: Action;
}

/**
 * Hard-coded context menu for ticket actions.
 * In the future make this a context menu component with context item children with callbacks.
 */
export default function ActionContextMenu(props: ActionContextMenuProps) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);

	const { control, handleSubmit, reset } = useForm<Ticket>({ defaultValues: {} });

	const navigate = useNavigate();

	const popover = (
		<Popover id="popover-basic">
			<ul style={{ overflow: "hidden", borderRadius: "0.3rem" }}>
				<li
					style={{ overflow: "hidden" }}
					className="flex items-center px-4 py-1 cursor-pointer hover:bg-gray-100"
					onClick={() => {
						setIsPopoverOpen(false);
						setIsModalOpen(true);
					}}
				>
					<GrChapterAdd className="mr-1" /> New ticket
				</li>
			</ul>
		</Popover>
	);

	function cancelEdit() {
		setIsModalOpen(false);
		reset();
	}

	async function formSubmit(newTicket: Ticket) {
		const actionCopy: Action = Object.assign({}, props.action);
		actionCopy.id = 0;
		actionCopy.ticketId = 0;
		actionCopy.attachments = undefined; // TODO: Implement attachment copying.

		newTicket.id = 0;
		newTicket.userId = props.action.userId;
		newTicket.customCreated = props.action.customCreated;
		newTicket.channelId = props.ticket.channelId;
		newTicket.actions = [actionCopy];

		const toastId = toast.loading("Creating ticket...");

		const res = await api.createEntity<Ticket>(Entities.TICKET, newTicket);

		if (res.successful && res.data != null) {
			const newTicketId = res.data.id;
			toast.success("Ticket created: #" + newTicketId, { id: toastId });
			navigate("/tickets/" + newTicketId);
		} else {
			toast.error("Could not create ticket. " + res.errorCode + ": " + res.errorMsg, { id: toastId });
		}
	}

	return (
		<>
			<OverlayTrigger
				rootClose
				// rootCloseEvent="mousedown"
				trigger="click"
				placement="bottom-end"
				overlay={popover}
				show={isPopoverOpen}
				onToggle={() => setIsPopoverOpen((prevState) => !prevState)}
			>
				<button
					className="flex items-center justify-center border border-gray-300 bg-white rounded p-1 w-7 h-7"
					onClick={() => {}}
				>
					<BiDotsHorizontalRounded />
				</button>
			</OverlayTrigger>

			<Modal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title="Create a new ticket from this action"
			>
				<ModalContent>
					<FormHotkeys onSubmit={handleSubmit(formSubmit)} onCancel={cancelEdit} />

					<div className="text-gray-700 italic">Attachments will not be copied.</div>

					<W_TextInput
						label="Subject"
						dataname="description"
						control={control}
						mandatory
						autoFocus
					/>
				</ModalContent>

				<ModalButtons>
					<Button onClick={cancelEdit} label="Cancel" />
					<ButtonBlue onClick={() => handleSubmit(formSubmit)()} label="Create" />
				</ModalButtons>
			</Modal>
		</>
	);
}
